import type { ClaimFieldOption } from './tariffConstants';

type Translate = (key: string, defaultValue?: string) => string;

export type SKClaimField =
  | 'claimType'
  | 'claimMode'
  | 'formType'
  | 'corporationIndicator'
  | 'specialCircumstances';

const skClaimFieldValues: Record<SKClaimField, readonly string[]> = {
  claimType: ['P', 'W', 'D'],
  claimMode: ['0', '1', '6', '9'],
  formType: ['8', 'E'],
  corporationIndicator: ['A', 'B', 'C', 'D'],
  specialCircumstances: ['TF', 'PF', 'CF', 'TA', 'VT'],
};

const getNoneOption = (translate: Translate): ClaimFieldOption => ({
  label: translate('pages:claims.provinceFields.none', 'None'),
  value: ' ',
});

export const getSKClaimFieldOptions = (
  field: SKClaimField,
  province: string | null | undefined,
  translate: Translate
): ClaimFieldOption[] => {
  const noneOption = getNoneOption(translate);
  if (province?.toUpperCase() !== 'SK') return [noneOption];

  return [
    noneOption,
    ...skClaimFieldValues[field].map((value) => ({ label: value, value })),
  ];
};

export const getServiceLocationIndicatorOptions = (
  province: string | null | undefined,
  translate: Translate
): ClaimFieldOption[] => {
  const noneOption = getNoneOption(translate);

  if (province?.toUpperCase() === 'SK') {
    return [
      {
        label: translate('pages:claims.provinceFields.regina', 'R - Regina'),
        value: 'R',
      },
      {
        label: translate(
          'pages:claims.provinceFields.saskatoon',
          'S - Saskatoon'
        ),
        value: 'S',
      },
      {
        label: translate(
          'pages:claims.provinceFields.ruralNorthernPremium',
          'X - Rural and Northern Premium'
        ),
        value: 'X',
      },
      noneOption,
    ];
  }

  return [
    {
      label: translate('pages:claims.provinceFields.clinic', 'C - Clinic'),
      value: 'C',
    },
    {
      label: translate(
        'pages:claims.provinceFields.outPatient',
        'O - Out Patient'
      ),
      value: 'O',
    },
    {
      label: translate(
        'pages:claims.provinceFields.emergencyDepartment',
        'E - Emergency Dept'
      ),
      value: 'E',
    },
    noneOption,
  ];
};

export type ClaimSelectField =
  | 'virtualPhysicianRegion'
  | 'optometristReason'
  | 'thirdPartyLiability'
  | 'incorporated';

const claimSelectOptions: Record<ClaimSelectField, ClaimFieldOption[]> = {
  virtualPhysicianRegion: [
    { label: 'Brandon', value: 'Brandon' },
    { label: 'Northern', value: 'Northern' },
    { label: 'Rural', value: 'Rural' },
    { label: 'Remote', value: 'Remote' },
    { label: 'Winnipeg', value: 'Winnipeg' },
    { label: 'Other', value: 'Other' },
  ],
  optometristReason: [
    { label: 'None', value: '00' },
    { label: 'Amblyopia', value: '15' },
    { label: 'Corneal Abrasion/Trauma', value: '16' },
    { label: 'Corneal Erosions', value: '17' },
    { label: 'Systemic Disease Effecting Eye', value: '18' },
    { label: 'Eye Infection/Inflammation', value: '19' },
    {
      label: 'Eye Muscle Imbalance, Children (<19 years of age)',
      value: '20',
    },
    { label: 'Glaucoma', value: '21' },
    { label: 'Cataract', value: '22' },
    { label: 'Intra-ocular Haemorrhages', value: '23' },
    { label: 'Post-op Eye Complications/Follow-up', value: '24' },
    { label: 'Significantly Changed Refractive Error', value: '25' },
    {
      label: 'Referral By Practitioner (specify condition in remarks)',
      value: '26',
    },
    {
      label: 'Child <19 y/o DX Myopia, Hyperopia, or Astigmatism',
      value: '27',
    },
    { label: 'Other (specify condition in remarks)', value: '28' },
  ],
  thirdPartyLiability: [
    { label: 'T', value: 'T' },
    { label: 'W', value: 'W' },
  ],
  incorporated: [
    { label: 'Incorporated', value: '1' },
    { label: 'Non-Incorporated', value: '0' },
  ],
};

export const getClaimSelectOptions = (
  field: ClaimSelectField,
  translate: Translate
): ClaimFieldOption[] =>
  claimSelectOptions[field].map((option) => ({
    ...option,
    label: translate(
      `pages:claims.selectOptions.${field}.${option.value}`,
      option.label
    ),
  }));
